import { createContext, useContext, useState, useCallback } from 'react';
import '../styles/Modal.css';

const ModalContext = createContext(null);

// Usage:
//   openModal({ title, text, confirm?: { label, onClick, danger? }, cancel?: { label } })
//
// - confirm omitted  → single "Ok" / dismiss button only
// - confirm present  → confirm button + cancel button
export function ModalProvider({ children }) {
  const [modal,   setModal]   = useState(null);
  const [closing, setClosing] = useState(false);

  const openModal = useCallback((options) => {
    setClosing(false);
    setModal(options);
  }, []);

  const closeModal = useCallback(() => setClosing(true), []);

  function handleAnimationEnd() {
    if (closing) {
      setModal(null);
      setClosing(false);
    }
  }

  async function handleConfirm() {
    if (modal?.confirm?.onClick) await modal.confirm.onClick();
    closeModal();
  }

  const hasConfirm = !!modal?.confirm;

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modal && (
        <div
          className={`modal-backdrop${closing ? ' closing' : ''}`}
          onClick={closeModal}
          onAnimationEnd={e => { if (e.target === e.currentTarget) handleAnimationEnd(); }}
        >
          <div className={`modal-box${closing ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
            {modal.title && (
              <div className="modal-header">
                <h2 className="modal-title">{modal.title}</h2>
              </div>
            )}
            <div className={`modal-body${!modal.title ? ' modal-body--no-header' : ''}`}>
              {modal.text    && <p className="modal-text">{modal.text}</p>}
              {modal.content && <div className="modal-content">{modal.content}</div>}
            </div>
            <div className="modal-footer">
              <div className="modal-actions">
                {hasConfirm ? (
                  <>
                    <button
                      className={`modal-btn modal-btn--confirm${modal.confirm.danger ? ' danger' : ''}`}
                      onClick={handleConfirm}
                    >
                      {modal.confirm.label}
                    </button>
                    <button className="modal-btn modal-btn--cancel" onClick={closeModal}>
                      {modal.cancel?.label || 'Cancel'}
                    </button>
                  </>
                ) : (
                  <button className="modal-btn modal-btn--confirm" onClick={closeModal}>
                    {modal.cancel?.label || 'Ok'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  return useContext(ModalContext);
}
